<template>
  <div class="view pm projects">
    <div class="page">
      <div class="header">
        <h3 class="text-black mt-2 mb-3"><strong>Projects Management</strong></h3>
      </div>
      <div class="content">
        <div class="card">
          <div class="card-header card-header-flex">
            <div class="d-flex flex-column justify-content-center mr-auto">
              <h5 class="mb-0">
                Projects List
                <span :key="meta.total" class="animated fadeIn">({{ meta.total }})</span>
              </h5>
            </div>

            <div class="d-flex flex-column justify-content-center">
              <span>
                <a-button type="default" icon="sync" class="mr-2" @click="fetch"> Refresh </a-button>
                <a-button type="primary" icon="user" @click="onNewProject"> New Project </a-button>
              </span>
            </div>
          </div>
          <!-- table -->
          <a-table
            :loading="loading"
            :columns="columns"
            :dataSource="projects"
            :rowKey="(record, index) => record._id"
            :scroll="{ x: 800 }"
            :pagination="pagination"
            @change="updateQuery"
          >
            <span slot="id" slot-scope="uid">
              <strong style="font-size: 1.15rem">{{ uid }}</strong>
            </span>

            <span slot="customer" slot-scope="customer" style="font-size: 1.1rem">
              <template v-if="customer">
                <span v-if="customer.name">{{ customer.name }}</span>
                <span v-if="customer.last_name" class="ml-1">{{ customer.last_name }}</span>
              </template>
            </span>

            <span slot="status" slot-scope="project">
              <a-tag class="status" :class="project.status" :color="project.status | PROJECT_STATUS_COLOR_">
                {{ project.status | PROJECT_STATUS_ }}
              </a-tag>
            </span>

            <span slot="wechat" slot-scope="customer">
              <span v-if="customer.wechat"> {{ customer.wechat }}</span>
              <span v-else>---</span>
            </span>

            <span slot="phone" slot-scope="customer">
              <span v-if="customer.phone"> {{ customer.phone }}</span>
              <span v-else>---</span>
            </span>

            <span slot="property" slot-scope="property">{{ property | PROPERTY_ }}</span>

            <span slot="services" slot-scope="services">
              <div class="services-group">
                <span v-for="service in services" :key="service">{{ service | SERVICE_LOCALE_(locale) }}</span>
              </div>
            </span>

            <span slot="description" slot-scope="description">
              <div class="sticker">{{ description || 'N/A' }}</div>
            </span>

            <span slot="created_at" slot-scope="created_at">
              <div class="inner" style="display: flex; flex-direction: column" v-if="created_at">
                <div>{{ created_at | moment('l') }}</div>
                <div style="font-size: 0.9rem">({{ $moment(created_at).fromNow() }})</div>
              </div>
            </span>

            <span slot="updated_at" slot-scope="updated_at">
              <div class="inner" style="display: flex; flex-direction: column" v-if="updated_at">
                <div>{{ updated_at | moment('l') }}</div>
                <div style="font-size: 0.9rem">({{ $moment(updated_at).fromNow() }})</div>
              </div>
            </span>

            <span slot="specialist" slot-scope="specialist">
              <span v-if="specialist && specialist.display_name"> {{ specialist.display_name }}</span>
            </span>

            <span slot="actions" slot-scope="project">
              <a-button type="primary" class="ml-1" @click="onEdit(project._id)" size="small" icon="edit"
                >Edit</a-button
              >
            </span>
          </a-table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import router from '@/router'
import options from '@/options'
import * as staffsService from '@/services/system/staffs'

const KEY = 'projects'

export default {
  data() {
    const locale = this.$i18n.locale ? (this.$i18n.locale === 'zh-CN' ? 'zh' : 'en') : 'en'
    return {
      staffs: [],
      columns: [
        {
          title: 'ID',
          dataIndex: 'uid',
          key: 'uid',
          width: 80,
          fixed: 'left',
          scopedSlots: { customRender: 'id' },
          defaultSortOrder: 'descend',
          sorter: (a, b) => a.uid - b.uid,
        },
        {
          title: 'Customer',
          dataIndex: 'customer',
          key: 'customer',
          width: 150,
          fixed: 'left',
          scopedSlots: { customRender: 'customer' },
        },
        {
          title: 'Status',
          key: 'status',
          width: 120,
          align: 'left',
          filters: options.PROJECT_STATUS.map((s) => {
            return { text: s.value, value: s.key }
          }),
          filterMultiple: false,
          scopedSlots: { customRender: 'status' },
        },
        {
          title: 'WeChat',
          dataIndex: 'customer',
          key: 'customer.wechat',
          width: 130,
          scopedSlots: { customRender: 'wechat' },
        },
        {
          title: 'Phone',
          dataIndex: 'customer',
          key: 'customer.phone',
          width: 120,
          scopedSlots: { customRender: 'phone' },
        },
        {
          title: 'Location',
          dataIndex: 'location',
          key: 'location',
          width: 130,
        },
        {
          title: 'Property',
          dataIndex: 'property',
          key: 'property',
          width: 110,
          filters: options.PROPERTY_TYPES.map((s) => {
            return { text: s.value, value: s.key }
          }),
          filterMultiple: false,
          scopedSlots: { customRender: 'property' },
        },
        {
          title: 'Services',
          dataIndex: 'services',
          key: 'services',
          width: 180,
          filters: options.SERVICE_TYPES.map((s) => {
            return { text: s.locales[locale], value: s.key }
          }),
          filterMultiple: false,
          scopedSlots: { customRender: 'services' },
        },
        {
          title: 'Description',
          dataIndex: 'description',
          key: 'description',
          width: 220,
          scopedSlots: { customRender: 'description' },
        },
        {
          title: 'Source',
          dataIndex: 'source',
          key: 'source',
          width: 150,
          filters: options.GENERAL_SOURCES.map((s) => {
            return { text: s.value, value: s.key }
          }),
          filterMultiple: false,
        },
        {
          title: 'Updated',
          dataIndex: 'updated_at',
          key: 'updated_at',
          align: 'center',
          width: 130,
          scopedSlots: { customRender: 'updated_at' },
          sorter: (a, b) => a.updated_at - b.updated_at,
        },
        {
          title: 'Created',
          dataIndex: 'created_at',
          key: 'created_at',
          align: 'center',
          width: 130,
          scopedSlots: { customRender: 'created_at' },
          sorter: (a, b) => a.created_at - b.created_at,
        },
        {
          title: 'Specialist',
          dataIndex: 'specialist',
          key: 'specialist',
          width: 120,
          filters: [],
          scopedSlots: { customRender: 'specialist' },
        },
        {
          title: 'Actions',
          key: 'actions',
          fixed: 'right',
          width: 120,
          scopedSlots: { customRender: 'actions' },
        },
      ],
    }
  },
  methods: {
    async fetch() {
      await this.$store.dispatch('pm/fetchProjects')
    },
    onNewProject() {
      this.$store.dispatch('pm/setNewProjectCustomer', null)
      this.$store.dispatch('pm/open', 'CREATE_PROJECT')
    },
    onEdit(id) {
      this.$router.push({ name: 'project-detail', params: { id } })
    },
    async updateQuery(pagination, filters, sorter) {
      const projects = await this.$store.dispatch('pm/updateQuery', {
        key: KEY,
        query: { pagination, filters, sorter },
      })
    },
  },
  computed: {
    loading() {
      return this.$store.getters['pm/loading'](KEY)
    },
    projects() {
      return this.$store.getters['pm/data'](KEY)
    },
    pagination() {
      return this.$store.getters['pm/pagination'](KEY)
    },
    meta() {
      return this.$store.getters['pm/meta'](KEY)
    },
    locale() {
      return this.$i18n.locale ? (this.$i18n.locale === 'zh-CN' ? 'zh' : 'en') : 'en'
    },
  },
  async created() {
    await this.fetch()
    this.staffs = await staffsService.fetchAllStaffs()
    if (this.staffs) {
      this.columns.find((c) => c.key === 'specialist').filters = this.staffs.map((s) => {
        return { text: s.display_name, value: s._id }
      })
    }
  },
  mounted() {
    // console.log(this.projects)
  },
  destroyed() {
    // store.dispatch('pm/init')
  },
}
</script>

<style lang="scss" scoped>
.pm.projects {
  .status {
    border: none;
    text-transform: capitalize;
    background: #666;
    color: #fff;
  }
  .dot {
    display: inline-block;
    height: 0.86rem;
    width: 0.86rem;
    background: #666;
    border-radius: 20px;
    vertical-align: middle;
    position: relative;
    top: -2px;

    &.active {
      background: #00b44b;
    }
  }

  .sticker {
    background: #ffeb3b;
    padding: 2px 8px;
    color: #000;
    border-radius: 3px;
    font-size: 12px;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    box-shadow: 0 0 10px #eee;
  }

  .services-group {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    gap: 1px;
    span {
      font-size: 12px;
    }
  }
}
</style>
